@if (!(deviceService.audioDeviceError$ | async); as error) {
  @switch (permissions.microphoneAccess()) {
    @case (null) {
      Loading...
    }
    @case ('prompt') {
      <andor-media-access-prompt
        [icon]="faHeadset"
        [text]="
          'This app requires access to your microphone to enable voice interaction for a smoother, more intuitive experience. Microphone access is solely used for functionality and is never recorded or shared. Please grant permission to unlock all features.'
            | translate
        "
        (onRequestAccess)="requestMic()"></andor-media-access-prompt>
    }
    @case ('denied') {
      <andor-media-access-denied
        [text]="
          'Microphone access is currently blocked. To enable voice chat, please adjust your browser settings to allow microphone access. This will unlock the voice interaction features of the app.'
            | translate
        "
        (onShowTutorial)="permissions.openPermissionTutorial()"></andor-media-access-denied>
    }
    @default {
      <div class="device-selection">
        <div class="audio-input">
          @if (deviceService.audioInputDeviceOptions$ | async; as audioInputDevices) {
            <andor-select-field
              [options]="audioInputDevices"
              [selectedOption]="deviceService.activeAudioInputDeviceOption$ | async"
              (selected)="deviceService.selectAudioInputDeviceById($event.id)"
              title="{{ 'Audio Input Device' | translate }}"></andor-select-field>
            <andor-mat-slider-wrapper label="{{ 'Input Volume' | translate }}">
              <mat-slider [max]="2" [min]="0" [step]="0.1" discrete [displayWith]="formatVolume">
                <input
                  matSliderThumb
                  (valueChange)="inputVolumeChanged($event)"
                  [ngModel]="inputVolume()" />
              </mat-slider>
            </andor-mat-slider-wrapper>
          }
        </div>
        <div class="audio-outputs">
          @if (deviceService.audioOutputDeviceOptions$ | async; as audioOutputDevices) {
            <andor-select-field
              [options]="audioOutputDevices"
              [selectedOption]="deviceService.activeAudioOutputDeviceOption$ | async"
              [forceDisabled]="!deviceService.audioOutputSelectionSupported"
              [infoTooltip]="
                deviceService.audioOutputSelectionSupported
                  ? ''
                  : ('Your browser doesn\'t support selecting the audio output device. Change it in your operating system or browser settings instead.'
                    | translate)
              "
              (selected)="deviceService.selectAudioOutputDeviceById($event.id)"
              title="{{ 'Audio Output Device' | translate }}"></andor-select-field>
          }
          <andor-mat-slider-wrapper label="{{ 'Output Volume' | translate }}">
            <mat-slider [max]="2" [min]="0" [step]="0.1" discrete [displayWith]="formatVolume">
              <input
                matSliderThumb
                (valueChange)="outputVolumeChanged($event)"
                [ngModel]="outputVolume()" />
            </mat-slider>
          </andor-mat-slider-wrapper>
        </div>
      </div>
      <andor-mat-slider-wrapper
        label="{{ 'Volume Gate' | translate }}"
        [value]="volumeGatePercent()"
        tooltipText="{{
          'Measures the volume of the input audio signal in dBFS, thus deciding when you are speaking loud enough to transmit voice data.'
            | translate
        }}">
        <mat-slider
          [max]="volumeGateMax"
          [min]="volumeGateMin"
          [step]="1"
          discrete
          [displayWith]="formatDBFS">
          <input matSliderThumb (valueChange)="updateVolumeGate($event)" [ngModel]="volumeGate()" />
        </mat-slider>
      </andor-mat-slider-wrapper>
      <andor-mat-toggle-wrapper
        label="{{ 'Automatic Gain Control' | translate }}"
        tooltipText="{{
          'Brings the audio input audio signal to an appropriate range when it\'s either too loud or too quiet.'
            | translate
        }}">
        <mat-slide-toggle
          (toggleChange)="updateAGC()"
          [checked]="autoGainControl()"></mat-slide-toggle>
      </andor-mat-toggle-wrapper>

      <andor-mat-toggle-wrapper
        label="{{ 'Voice Activity Detection' | translate }}"
        tooltipText="{{
          'Analyzes the audio input signal using smart voice detection algorithms to determine the presence of speech.'
            | translate
        }}">
        <mat-slide-toggle
          (toggleChange)="updateVad()"
          [checked]="voiceActivityDetection()"></mat-slide-toggle>
      </andor-mat-toggle-wrapper>

      <andor-mat-toggle-wrapper
        label="{{ 'Echo Cancellation' | translate }}"
        tooltipText="{{
          'Subtract echoes, reverberation, and unwanted added sounds from the audio input signal.'
            | translate
        }}">
        <mat-slide-toggle
          (toggleChange)="updateEC()"
          [checked]="echoCancellation()"></mat-slide-toggle>
      </andor-mat-toggle-wrapper>

      <andor-mat-toggle-wrapper
        label="{{ 'Noise Suppression' | translate }}"
        tooltipText="{{
          'Removes distracting background noise from the input audio signal.' | translate
        }}">
        <mat-slide-toggle
          (toggleChange)="updateNoiseSuppression()"
          [checked]="noiseSuppression()"></mat-slide-toggle>
      </andor-mat-toggle-wrapper>

      @if (showTestComponents) {
        <hr />

        <andor-mat-toggle-wrapper
          label="{{ 'Voice Isolation' | translate }}"
          tooltipText="{{
            'Uses an advanced filter to prioritizes your voice and blocks ambient noises.'
              | translate
          }}">
          <mat-slide-toggle
            (toggleChange)="onVoiceIsolationChange()"
            [checked]="voiceIsolation()"></mat-slide-toggle>
        </andor-mat-toggle-wrapper>

        @if (voiceIsolation()) {
          <andor-mat-slider-wrapper
            label="{{ 'Noise Attenuation' | translate }}: {{ voiceIsolationAttenuationLimit() }} dB"
            tooltipText="{{
              'Sets the maximum amount of noise reduction. Higher values suppress more noise but may distort speech.'
                | translate
            }}">
            <mat-slider [max]="100" [min]="0" [step]="1" discrete>
              <input
                matSliderThumb
                (valueChange)="onVoiceIsolationAttenuationLimitChange($event)"
                [ngModel]="voiceIsolationAttenuationLimit()" />
            </mat-slider>
          </andor-mat-slider-wrapper>

          <andor-mat-slider-wrapper
            label="{{ 'Post Filter Beta' | translate }}: {{ voiceIsolationPostFilterBeta() }}"
            tooltipText="{{
              'Fine-tunes the output after initial denoising. Higher values clean up residual noise but may affect naturalness.'
                | translate
            }}">
            <mat-slider [max]="1.0" [min]="0.0" [step]="0.01" discrete>
              <input
                matSliderThumb
                (valueChange)="onVoiceIsolationPostFilterBetaChange($event)"
                [ngModel]="voiceIsolationPostFilterBeta()" />
            </mat-slider>
          </andor-mat-slider-wrapper>
        }
      }
    }
  }
} @else {
  <div class="device-error">{{ deviceService.audioDeviceError$ | async }}</div>
  <andor-button (click)="deviceService.refreshAudioInputDevices()">
    {{ 'Test Audio' | translate }}
  </andor-button>
}
